import { useEffect, useRef, useState } from 'react';
import api from '@/api';
import {
  Download,
  Eye,
  FileImage,
  FileQuestion,
  FileSpreadsheet,
  FileText,
  LoaderCircle,
  X,
} from 'lucide-react';

const extension = (name = '') => {
  const index = name.lastIndexOf('.');
  return index >= 0 ? name.slice(index).toLowerCase() : '';
};

export const getFileKind = (name = '') => {
  const ext = extension(name);
  if (['.jpg', '.jpeg', '.png'].includes(ext)) return 'image';
  if (ext === '.pdf') return 'pdf';
  if (ext === '.txt') return 'text';
  if (['.doc', '.docx'].includes(ext)) return 'document';
  if (ext === '.xlsx') return 'spreadsheet';
  return 'unsupported';
};

const fileLabel = (name = '') => extension(name).replace('.', '').toUpperCase() || 'ФАЙЛ';

const kindIcon = {
  image: FileImage,
  pdf: FileText,
  text: FileText,
  document: FileText,
  spreadsheet: FileSpreadsheet,
  unsupported: FileQuestion,
};

export async function loadFilePreview(file, downloadUrl, previewUrl) {
  const kind = getFileKind(file.original_name);
  if (kind === 'image' || kind === 'pdf') {
    const response = await api.get(downloadUrl, { responseType: 'blob' });
    return {
      file,
      kind,
      url: URL.createObjectURL(response.data),
      loading: false,
    };
  }

  const { data } = await api.get(previewUrl);
  return { file, kind: data.kind || kind, ...data, loading: false };
}

export function FileThumbnail({ file, downloadUrl, onOpen }) {
  const kind = getFileKind(file.original_name);
  const Icon = kindIcon[kind] || FileQuestion;
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(kind === 'image');

  useEffect(() => {
    if (kind !== 'image') return undefined;
    let disposed = false;
    let objectUrl = '';

    api.get(downloadUrl, { responseType: 'blob' })
      .then(({ data }) => {
        objectUrl = URL.createObjectURL(data);
        if (disposed) URL.revokeObjectURL(objectUrl);
        else setImageUrl(objectUrl);
      })
      .catch(() => {})
      .finally(() => { if (!disposed) setLoading(false); });

    return () => {
      disposed = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [downloadUrl, kind]);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group/preview relative flex h-28 w-full items-center justify-center overflow-hidden bg-stone-950/70 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-inset"
      aria-label={`Предпросмотр файла ${file.original_name}`}
    >
      {kind === 'image' && imageUrl ? (
        <img src={imageUrl} alt="" className="h-full w-full object-cover" />
      ) : loading ? (
        <div className="h-full w-full animate-pulse bg-stone-800/60 motion-reduce:animate-none" aria-hidden="true" />
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Icon size={30} className={kind === 'spreadsheet' ? 'text-emerald-500/70' : kind === 'pdf' ? 'text-red-400/70' : 'text-stone-500'} />
          <span className="text-[10px] font-semibold tracking-[0.16em] text-stone-600">{fileLabel(file.original_name)}</span>
        </div>
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-stone-950/0 opacity-0 transition-[opacity,background-color] duration-200 group-hover/preview:bg-stone-950/45 group-hover/preview:opacity-100 group-focus-visible/preview:bg-stone-950/45 group-focus-visible/preview:opacity-100">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-stone-100/95 px-2.5 py-1.5 text-xs font-medium text-stone-900 shadow-sm">
          <Eye size={14} /> Открыть
        </span>
      </span>
    </button>
  );
}

export function FilePreviewOverlay({ preview, onClose, onDownload, onRetry }) {
  const closeButtonRef = useRef(null);
  const [sheetIndex, setSheetIndex] = useState(0);

  useEffect(() => {
    setSheetIndex(0);
    closeButtonRef.current?.focus();
  }, [preview?.file?.id]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);

  if (!preview) return null;
  const sheets = preview.sheets || [];
  const activeSheet = sheets[sheetIndex] || sheets[0];

  return (
    <div
      className="fixed inset-0 z-[220] flex flex-col bg-stone-950/95"
      role="dialog"
      aria-modal="true"
      aria-label={`Предпросмотр файла ${preview.file?.original_name || ''}`}
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="flex min-h-14 items-center gap-3 border-b border-stone-800 bg-stone-900 px-3 sm:px-5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-stone-200">{preview.file?.original_name}</p>
          <p className="text-[11px] text-stone-500">Предпросмотр</p>
        </div>
        <button
          type="button"
          onClick={() => onDownload(preview.file)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-700 px-3 text-xs font-medium text-stone-300 transition-colors hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
        >
          <Download size={14} /> <span className="hidden sm:inline">Скачать</span>
        </button>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-stone-800 hover:text-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
          aria-label="Закрыть предпросмотр"
        >
          <X size={19} />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-3 sm:p-6">
        {preview.loading ? (
          <div className="flex flex-col items-center gap-3 text-stone-400" role="status">
            <LoaderCircle size={28} className="animate-spin motion-reduce:animate-none" />
            <span className="text-sm">Готовим предпросмотр</span>
          </div>
        ) : preview.error ? (
          <div className="max-w-sm text-center">
            <FileQuestion size={38} className="mx-auto mb-3 text-stone-600" />
            <p className="text-sm font-medium text-stone-300">Не удалось открыть файл</p>
            <p className="mt-1 text-xs leading-relaxed text-stone-500">{preview.error}</p>
            {onRetry && (
              <button type="button" onClick={() => onRetry(preview.file)} className="mt-4 rounded-md bg-amber-700 px-3 py-2 text-xs font-medium text-stone-100 hover:bg-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
                Повторить
              </button>
            )}
          </div>
        ) : preview.kind === 'image' ? (
          <img src={preview.url} alt={preview.file?.original_name || ''} className="max-h-full max-w-full rounded-md object-contain shadow-2xl" />
        ) : preview.kind === 'pdf' ? (
          <iframe src={preview.url} className="h-full min-h-[70vh] w-full max-w-6xl rounded-md bg-stone-100" title={preview.file?.original_name || 'PDF'} />
        ) : preview.kind === 'spreadsheet' ? (
          <div className="flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-md border border-stone-800 bg-stone-900">
            {sheets.length > 1 && (
              <div className="flex gap-1 overflow-x-auto border-b border-stone-800 p-2">
                {sheets.map((sheet, index) => (
                  <button key={`${sheet.name}-${index}`} type="button" onClick={() => setSheetIndex(index)}
                    className={`shrink-0 rounded px-2.5 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 ${index === sheetIndex ? 'bg-amber-700 text-stone-100' : 'text-stone-400 hover:bg-stone-800'}`}>
                    {sheet.name}
                  </button>
                ))}
              </div>
            )}
            {activeSheet?.rows?.length ? (
              <div className="min-h-0 flex-1 overflow-auto">
                <table className="min-w-full border-collapse text-xs text-stone-300">
                  <tbody>
                    {activeSheet.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex === 0 ? 'bg-stone-800/70 font-medium text-stone-200' : 'border-t border-stone-800/70'}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex} className="max-w-72 whitespace-pre-wrap border-r border-stone-800/70 px-3 py-2 align-top">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="m-auto text-sm text-stone-500">В листе нет данных</p>
            )}
          </div>
        ) : preview.kind === 'text' || preview.kind === 'document' ? (
          <div className="h-full w-full max-w-4xl overflow-auto rounded-md border border-stone-800 bg-stone-900 p-5 sm:p-8">
            {preview.text ? (
              <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-7 text-stone-300">{preview.text}</pre>
            ) : (
              <p className="text-center text-sm text-stone-500">В файле нет текста для показа</p>
            )}
          </div>
        ) : (
          <div className="max-w-sm text-center">
            <FileQuestion size={40} className="mx-auto mb-3 text-stone-600" />
            <p className="text-sm font-medium text-stone-300">Предпросмотр недоступен</p>
            <p className="mt-1 text-xs leading-relaxed text-stone-500">{preview.message || 'Этот формат пока можно только скачать.'}</p>
          </div>
        )}
      </div>

      {preview.truncated && (
        <div className="border-t border-stone-800 bg-stone-900 px-4 py-2 text-center text-xs text-stone-500">
          Показана только часть большого файла. Полная версия доступна при скачивании.
        </div>
      )}
    </div>
  );
}
